import { CommunityBodyEditor } from "../components/community/content/CommunityBodyEditor";
import { CommunityBody } from "../components/community/content/CommunityBody";
import { CONTENT_KINDS, contentRequest, activeBlocks, type CommunityContentBlock, type ContentKind } from "../lib/communityContent";
import {
  COMMUNITY_PURPOSES,
  readCommunityPurpose,
  purposeFromTags,
  withPurposeTag,
} from "../lib/communityParticipation";
import { communityToolDraft } from "../lib/communityToolDraft";
import { TOOL_DIRECTORY } from "../app/toolDirectory";
import "../styles/community-renewal.css";
import * as React from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Paperclip,
  Save,
  NotebookPen,
  Eye,
  Check,
} from "lucide-react";
import {
  apiClient,
  CommunityCategory,
  CommunityPostDetail,
  getMediaUrl,
} from "../lib/api";
import {
  saveCommunityDraft,
  getCommunityDraft,
  deleteCommunityDraft,
} from "../lib/supabaseCommunity";
import { useAuth } from "../contexts/AuthContext";
import { usePageSEO } from "../lib/usePageSEO";
import { extractYouTubeVideoId } from "../lib/extractYouTubeVideoId";

/**
 * 이미지 압축 및 리사이즈 함수
 * 최대 가로 1920px, JPEG 품질 85%로 최적화하여 파일 크기 감소
 */
async function compressImage(
  file: File,
  maxWidth: number = 1920,
  quality: number = 0.85,
): Promise<File> {
  return new Promise((resolve, reject) => {
    // 이미지가 아닌 경우 원본 반환
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    // 파일 크기가 500KB 미만이면 압축하지 않음 (이미 작은 파일)
    if (file.size < 500 * 1024) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // 원본 크기 확인
        const originalWidth = img.width;
        const originalHeight = img.height;

        // 최대 크기보다 작으면 압축만 수행
        if (originalWidth <= maxWidth) {
          // 품질만 조정하여 압축
          const canvas = document.createElement("canvas");
          canvas.width = originalWidth;
          canvas.height = originalHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(file);
            return;
          }
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(file);
                return;
              }
              const compressedFile = new File(
                [blob],
                file.name.replace(/\.[^/.]+$/, "") + ".jpg",
                {
                  type: "image/jpeg",
                  lastModified: Date.now(),
                },
              );
              resolve(compressedFile);
            },
            "image/jpeg",
            quality,
          );
          return;
        }

        // 리사이즈 필요
        const aspectRatio = originalHeight / originalWidth;
        const newWidth = maxWidth;
        const newHeight = Math.round(maxWidth * aspectRatio);

        const canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(file);
          return;
        }

        // 고품질 리사이즈
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        // JPEG로 변환 및 압축
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, ".jpg"),
              {
                type: "image/jpeg",
                lastModified: Date.now(),
              },
            );
            resolve(compressedFile);
          },
          "image/jpeg",
          quality,
        );
      };
      img.onerror = () => resolve(file); // 에러 시 원본 반환
      img.src = e.target?.result as string;
    };
    reader.onerror = () => resolve(file); // 에러 시 원본 반환
    reader.readAsDataURL(file);
  });
}

function ensureHtml(v: string) {
  const s = (v || "").trim();
  if (!s) return "";
  // 이미 HTML이면 그대로
  if (s.includes("<") && s.includes(">")) return s;
  // plain text -> p 태그로
  const escaped = s
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
  return escaped
    .split("\n")
    .map((line) => (line.trim() ? `<p>${line}</p>` : "<p><br/></p>"))
    .join("");
}

// 미리보기용 HTML sanitize 및 YouTube 임베드 변환
function sanitizeHtmlForPreview(html: string) {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    // 기존 iframe 제거 (안전을 위해)
    doc.querySelectorAll("script,iframe").forEach((n) => n.remove());
    doc.querySelectorAll("*").forEach((el) => {
      Array.from(el.attributes).forEach((attr) => {
        if (attr.name.toLowerCase().startsWith("on"))
          el.removeAttribute(attr.name);
      });
    });
    // 이미지와 링크의 상대 경로를 절대 경로로 변환
    doc.querySelectorAll("img").forEach((img) => {
      const src = img.getAttribute("src");
      if (src) {
        img.setAttribute("src", getMediaUrl(src));
      }
    });

    // YouTube 링크를 iframe으로 변환
    doc.querySelectorAll("a").forEach((a) => {
      const href = a.getAttribute("href");
      if (!href) return;

      // 상대 경로 처리
      if (href.startsWith("/media/") || href.startsWith("/static/")) {
        a.setAttribute("href", getMediaUrl(href));
        return;
      }

      // YouTube URL인 경우 iframe으로 변환
      const youtubeId = extractYouTubeVideoId(href);
      if (youtubeId) {
        const iframe = doc.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${youtubeId}`;
        iframe.width = "560";
        iframe.height = "315";
        iframe.frameBorder = "0";
        iframe.allow =
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";
        iframe.allowFullscreen = true;
        iframe.style.maxWidth = "100%";
        iframe.style.aspectRatio = "16/9";

        // 링크를 iframe으로 교체
        const wrapper = doc.createElement("div");
        wrapper.className = "youtube-embed-wrapper";
        wrapper.style.margin = "1rem 0";
        wrapper.style.position = "relative";
        wrapper.style.paddingBottom = "56.25%"; // 16:9
        wrapper.style.height = "0";
        wrapper.style.overflow = "hidden";
        iframe.style.position = "absolute";
        iframe.style.top = "0";
        iframe.style.left = "0";
        iframe.style.width = "100%";
        iframe.style.height = "100%";
        wrapper.appendChild(iframe);

        a.parentNode?.replaceChild(wrapper, a);
      }
    });

    return doc.body.innerHTML;
  } catch {
    return html;
  }
}

export function CommunityPostForm() {
  const { isAuthenticated } = useAuth();
  const nav = useNavigate();
  const { postId } = useParams();
  const [searchParams] = useSearchParams();
  const editing = !!postId;
  const id = postId ? Number(postId) : null;
  const shareKind = searchParams.get("contentKind");
  const shareSourceId = searchParams.get("sourceId");
  const sharing = !editing && !!shareSourceId && CONTENT_KINDS.some(k => k.kind === shareKind);
  const toolDraft = editing ? null : communityToolDraft(searchParams.get("tool"), searchParams.get("purpose"));
  const draftKey = editing && id ? `community-draft-${id}`
    : sharing ? `community-draft-share-${shareKind}-${shareSourceId}` : toolDraft?.draftKey || "community-draft-new";

  const [categories, setCategories] = React.useState<CommunityCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [draftPending, setDraftPending] = React.useState(!editing);
  const [draftCheckFailed, setDraftCheckFailed] = React.useState(false);
  const [draftCheckRetry, setDraftCheckRetry] = React.useState(0);
  const [draftStatus, setDraftStatus] = React.useState("");
  const [imageCompressing, setImageCompressing] = React.useState(false);
  const imageSelectionVersion = React.useRef(0);
  const submitPending = React.useRef(false);
  const draftSaveQueue = React.useRef<Promise<void>>(Promise.resolve());
  const [draftInfo, setDraftInfo] = React.useState<{
    exists: boolean;
    savedAt?: string;
  } | null>(null);

  const [title, setTitle] = React.useState(toolDraft?.title || "");
  const [categoryId, setCategoryId] = React.useState<number | "">("");
  const [subcategoryId, setSubcategoryId] = React.useState<number | "">("");
  const [contentBlocks, setContentBlocks] = React.useState<CommunityContentBlock[]>([]);
  const [initialContent, setInitialContent] = React.useState<{kind:ContentKind;sourceId?:string}|undefined>(()=>{
    const kind=searchParams.get('contentKind');
    return !editing && CONTENT_KINDS.some(k=>k.kind===kind) ? {kind:kind as ContentKind,sourceId:searchParams.get('sourceId')||undefined}:undefined;
  });
  const consumeInitialContent = React.useCallback(()=>setInitialContent(undefined),[]);
  const contextTool = TOOL_DIRECTORY.find(
    (t) => t.route === searchParams.get("tool"),
  );
  const [purpose, setPurpose] = React.useState(() =>
    editing ? "" : readCommunityPurpose(searchParams.get("purpose")),
  );
  const purposeInfo = COMMUNITY_PURPOSES.find((p) => p.id === purpose);
  const [tags, setTags] = React.useState(() =>
    !editing && contextTool ? contextTool.name : "",
  );
  const [contentHtml, setContentHtml] = React.useState(toolDraft?.html || "");
  const [vibeCodingLink, setVibeCodingLink] = React.useState("");
  const [preview, setPreview] = React.useState(false);

  const [headImage, setHeadImage] = React.useState<File | null>(null);
  const [fileUpload, setFileUpload] = React.useState<File | null>(null);
  const [additionalFiles, setAdditionalFiles] = React.useState<File[]>([]);

  const [existing, setExisting] = React.useState<CommunityPostDetail | null>(
    null,
  );

  usePageSEO({
    title: editing
      ? "글 수정 - Foreducator 소통공간"
      : "글쓰기 - Foreducator 소통공간",
    description: editing
      ? "소통공간 게시글을 수정합니다."
      : "소통공간에 새 글을 작성합니다.",
    path:
      editing && id && !Number.isNaN(id)
        ? `/community/board/${id}/edit`
        : "/community/board/new",
    keywords: "교사 커뮤니티, 글쓰기, Foreducator 소통공간",
  });

  React.useEffect(() => {
    apiClient
      .listCommunityCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  // 목록에서 카테고리 선택 후 글쓰기로 왔을 때 URL의 category_id, subcategory_id로 초기 선택
  React.useEffect(() => {
    if (editing || categories.length === 0) return;
    const cid = searchParams.get("category_id");
    const sid = searchParams.get("subcategory_id");
    if (!cid) return;
    const catId = Number(cid);
    if (!Number.isNaN(catId) && categories.some((c) => c.id === catId)) {
      setCategoryId(catId);
      if (sid) {
        const subId = Number(sid);
        const cat = categories.find((c) => c.id === catId);
        if (
          !Number.isNaN(subId) &&
          cat?.subcategories?.some((s) => s.id === subId)
        ) {
          setSubcategoryId(subId);
        }
      } else {
        setSubcategoryId("");
      }
    }
  }, [editing, categories, searchParams]);

  const applyDraft = React.useCallback((draft: any) => {
    setPurpose(readCommunityPurpose(draft.purpose) || purposeFromTags(draft.tags));
    if (draft.title != null) setTitle(String(draft.title));
    if (draft.tags != null) setTags(String(draft.tags));
    if (draft.categoryId != null) setCategoryId(draft.categoryId);
    if (draft.subcategoryId != null) setSubcategoryId(draft.subcategoryId);
    if (draft.contentHtml != null) setContentHtml(String(draft.contentHtml));
    setContentBlocks(Array.isArray(draft.contentBlocks) ? draft.contentBlocks : []);
    if (draft.vibeCodingLink != null) setVibeCodingLink(String(draft.vibeCodingLink));
  }, []);

  // 임시저장 복구 (새 글일 때만 자동 안내)
  React.useEffect(() => {
    if (editing || !isAuthenticated) return;
    setDraftCheckFailed(false);
    let isMounted = true;
    getCommunityDraft(draftKey)
      .then((parsed) => {
        if (!isMounted) return;
        if (sharing) {
          if (parsed) {
            applyDraft(parsed);
            const connected = activeBlocks(String(parsed.contentHtml || ""), Array.isArray(parsed.contentBlocks) ? parsed.contentBlocks : []);
            if (connected.some(block => block.kind === shareKind && block.sourceId === shareSourceId)) setInitialContent(undefined);
          }
          setDraftInfo({ exists: false });
          setDraftPending(false);
          setDraftStatus(parsed ? "이 콘텐츠로 작성하던 글을 이어서 열었어요." : "");
        } else if (parsed) {
          setDraftInfo({ exists: true, savedAt: parsed?.savedAt || undefined });
        } else {
          setDraftInfo({ exists: false });
          setDraftPending(false);
        }
      })
      .catch(() => {
        if (!isMounted) return;
        setDraftInfo({ exists: false });
        setDraftCheckFailed(true);
        setDraftStatus(
          "임시저장을 확인하지 못했어요. 기존 글을 보호하기 위해 자동 저장을 잠시 멈췄어요.",
        );
      });
    return () => {
      isMounted = false;
    };
  }, [draftKey, editing, isAuthenticated, draftCheckRetry, sharing, shareKind, shareSourceId, applyDraft]);

  React.useEffect(() => {
    setExisting(null);
    setError("");
    setHeadImage(null);
    setFileUpload(null);
    setAdditionalFiles([]);
    imageSelectionVersion.current += 1;
    setImageCompressing(false);
    if (!editing) {
      setLoading(false);
      return () => {
        imageSelectionVersion.current += 1;
      };
    }
    if (!id || !Number.isSafeInteger(id) || id < 1) {
      setError("올바르지 않은 게시글 주소입니다.");
      setLoading(false);
      return;
    }
    setLoading(true);
    let active = true;
    apiClient
      .getCommunityPost(id)
      .then(async (p) => {
        const connected = p.content.includes('data-community-block') ? await contentRequest<{blocks:CommunityContentBlock[]}>('editor',{postId:id}) : {blocks:[]};
        if (!active) return;
        setContentBlocks(connected.blocks);
        setExisting(p);
        setTitle(p.title);
        setTags(p.tags || "");
        setPurpose(purposeFromTags(p.tags));
        setCategoryId(p.category_new?.id || "");
        setSubcategoryId(p.subcategory?.id || "");
        setContentHtml(ensureHtml(p.content));
        setVibeCodingLink(p.vibe_coding_link || "");
      })
      .catch((e: unknown) => {
        if (active)
          setError(e instanceof Error ? e.message : "불러오기에 실패했습니다.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
      imageSelectionVersion.current += 1;
    };
  }, [editing, id]);

  const subcategories = React.useMemo(() => {
    if (!categoryId) return [];
    return categories.find((c) => c.id === categoryId)?.subcategories || [];
  }, [categories, categoryId]);

  const isVibeCodingCategory = Boolean(
    (categoryId &&
      categories.find((c) => c.id === categoryId)?.name === "바이브코딩") ||
    (subcategoryId &&
      subcategories.some(
        (s) => s.id === subcategoryId && s.name === "바이브코딩",
      )),
  );

  const submit = async () => {
    if (
      submitPending.current ||
      imageCompressing ||
      loading ||
      (editing && existing?.id !== id)
    )
      return;
    if (!isAuthenticated) {
      setError("로그인이 필요합니다.");
      return;
    }
    if (!title.trim() || (toolDraft && title.trim() === toolDraft.title.trim())) {
      setError(toolDraft ? "도구 이름 뒤에 궁금한 점이나 나누고 싶은 경험을 적어주세요." : "제목을 입력해주세요.");
      return;
    }
    submitPending.current = true;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        tags: withPurposeTag(tags, purpose),
        category_id: categoryId,
        subcategory_id: subcategoryId,
        content: ensureHtml(contentHtml),
        content_blocks: activeBlocks(contentHtml,contentBlocks),
        head_image: headImage,
        file_upload: fileUpload,
        additional_files: additionalFiles,
        vibe_coding_link: isVibeCodingCategory
          ? vibeCodingLink.trim() || null
          : null,
      };
      const post =
        editing && id
          ? await apiClient.updateCommunityPost(id, payload)
          : await apiClient.createCommunityPost(payload);
      await draftSaveQueue.current.catch(() => {});
      // A successful post must not be presented as failed if draft cleanup is unavailable.
      if (!draftPending) await deleteCommunityDraft(draftKey).catch(() => {});
      nav(`/community/board/${post.id}`);
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : "저장에 실패했습니다.");
    } finally {
      submitPending.current = false;
      setSaving(false);
    }
  };

  React.useEffect(() => {
    if (
      !isAuthenticated ||
      saving ||
      loading ||
      draftPending ||
      (editing && !existing)
    )
      return;
    if (
      !title.trim() &&
      !contentHtml.trim() &&
      !tags.trim() &&
      !vibeCodingLink.trim()
    )
      return;
    let active = true;
    setDraftStatus("변경사항 저장 중…");
    const timer = window.setTimeout(async () => {
      const payload = {
        savedAt: new Date().toISOString(),
        purpose,
        title,
        categoryId,
        subcategoryId,
        tags,
        contentHtml,
        contentBlocks,
        vibeCodingLink,
      };
      try {
        const operation = draftSaveQueue.current
          .catch(() => {})
          .then(async () => {
            if (active) await saveCommunityDraft(draftKey, payload);
          });
        draftSaveQueue.current = operation;
        await operation;
        if (active)
          setDraftStatus(
            `자동 저장됨 · ${new Date(payload.savedAt).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}`,
          );
      } catch {
        if (active)
          setDraftStatus(
            "임시저장에 실패했어요. 작성 내용을 복사해 보관해 주세요.",
          );
      }
    }, 2000);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    isAuthenticated,
    saving,
    loading,
    draftPending,
    editing,
    existing,
    title,
    purpose,
    categoryId,
    subcategoryId,
    tags,
    contentHtml,
    contentBlocks,
    vibeCodingLink,
    draftKey,
  ]);

  const restoreDraft = async () => {
    try {
      const draft = await getCommunityDraft(draftKey);
      if (draft) applyDraft(draft);
      setDraftInfo({ exists: false });
      setDraftPending(false);
      setDraftStatus(draft ? "임시저장한 글을 불러왔어요." : "");
    } catch {
      setError("임시저장한 글을 불러오지 못했어요. 다시 시도해 주세요.");
    }
  };

  const clearDraft = async () => {
    try {
      await deleteCommunityDraft(draftKey);
    } catch {
      setError("임시저장을 지우지 못했어요. 다시 시도해 주세요.");
      return;
    }
    setDraftInfo({ exists: false });
    setDraftPending(false);
  };

  if (!isAuthenticated) {
    const returnPath =
      editing && id
        ? `/community/board/${id}/edit`
        : `/community/board/new${searchParams.size ? `?${searchParams}` : ""}`;
    return (
      <div className="fe-page-canvas fe-community">
        <div className="fe-community-layout fe-community-editor-layout">
          <Link to="/community/board" className="fe-community-back">
            <ArrowLeft size={16} />
            소통공간으로
          </Link>
          <div className="fe-community-empty">
            <div className="fe-community-empty-icon">
              <NotebookPen size={28} />
            </div>
            <h1 className="text-2xl font-bold">이야기를 함께 나눠요</h1>
            <p>로그인하면 글을 작성하고 자료를 공유할 수 있어요.</p>
            <Link
              to={`/login?next=${encodeURIComponent(returnPath)}`}
              className="fe-button fe-button-dark"
            >
              로그인하고 글쓰기
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fe-page-canvas fe-community">
      <div className="fe-community-layout fe-community-editor-layout">
        <Link to="/community/board" className="fe-community-back">
          <ArrowLeft size={16} />
          소통공간으로
        </Link>
        <header className="fe-community-page-heading">
          <div className="fe-community-heading-icon">
            <NotebookPen size={27} />
          </div>
          <div>
            <p className="fe-eyebrow">SHARE YOUR STORY</p>
            <h1>
              {editing ? "이야기 수정" : "이야기 쓰기"}
              <span className="fe-heading-dot">.</span>
            </h1>
            <p>수업의 발견, 작은 질문, 함께 나누고 싶은 자료를 남겨주세요.</p>
          </div>
        </header>
        {error && (
          <div role="alert" className="mb-4 text-sm text-red-600">
            {error}
          </div>
        )}
        {draftCheckFailed && (
          <div className="fe-community-draft" role="alert">
            <p>
              임시저장한 글을 확인하지 못했어요. 작성한 내용을 복사해 보관해
              주세요.
            </p>
            <button
              className="fe-community-button mt-3"
              onClick={() => setDraftCheckRetry((n) => n + 1)}
            >
              임시저장 다시 확인
            </button>
          </div>
        )}
        {!editing && draftPending && draftInfo?.exists && (
          <div className="fe-community-draft">
            <div className="text-sm font-semibold text-amber-900">
              임시저장된 글이 있어요
            </div>
            <div className="text-xs text-amber-800 mt-1">
              {draftInfo.savedAt
                ? `마지막 저장: ${new Date(draftInfo.savedAt).toLocaleString("ko-KR")}`
                : "마지막 저장 시각을 알 수 없어요."}
            </div>
            <div className="mt-3 flex gap-2">
              <button
                onClick={restoreDraft}
                className="fe-button fe-button-dark"
              >
                불러오기
              </button>
              <button onClick={clearDraft} className="fe-community-button">
                새로 작성
              </button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="fe-surface bg-white rounded-3xl border border-gray-200 shadow-sm p-6 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-3" />
            <div className="h-4 bg-gray-200 rounded w-full mb-2" />
            <div className="h-32 bg-gray-200 rounded w-full" />
          </div>
        ) : editing && !existing ? (
          <div className="fe-community-empty">
            <h2>수정할 글을 불러오지 못했어요</h2>
            <p>게시글 주소를 확인하거나 목록으로 돌아가 다시 선택해 주세요.</p>
            <Link to="/community/board" className="fe-community-button">
              이야기 목록
            </Link>
          </div>
        ) : (
          <div className="fe-community-editor">
            <section
              className="fe-community-writing-start"
              aria-label="글의 목적 선택"
            >
              <h2>어떤 이야기를 나눌까요?</h2>
              <div
                className="fe-community-purpose-tabs"
                role="group"
                aria-label="글의 목적"
              >
                {COMMUNITY_PURPOSES.map((item) => (
                  <button
                    type="button"
                    key={item.id}
                    aria-pressed={purpose === item.id}
                    onClick={() => {
                      const previous = communityToolDraft(contextTool?.route || null, purpose);
                      const next = communityToolDraft(contextTool?.route || null, item.id);
                      if (!editing && previous && next && contentHtml === previous.html) setContentHtml(next.html);
                      setPurpose(item.id);
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {!editing && contextTool && (
                <p className="fe-community-tool-context">
                  <Link to={contextTool.route}>{contextTool.name}</Link>에 관한 이야기예요.
                  {toolDraft
                    ? " 제목에 이야기할 내용을 이어 적고, 본문의 질문에 답해보세요. 도구 카드와 작성 틀은 자유롭게 수정하거나 지울 수 있어요."
                    : " 공유할 내용을 확인하고 본문에 추가해 주세요."}
                </p>
              )}
              {purposeInfo && (
                <details className="fe-community-writing-guide">
                  <summary>작성 도움 · 틀 넣기</summary>
                  <strong>{purposeInfo.title}</strong>
                  <p>{purposeInfo.guidance}</p>
                  {!contentHtml
                    .replace(/<[^>]*>/g, "")
                    .replace(/&nbsp;/g, "")
                    .trim() && (
                    <button
                      type="button"
                      className="fe-community-button"
                      onClick={() => setContentHtml(purposeInfo.template)}
                    >
                      작성 틀 넣기
                    </button>
                  )}
                </details>
              )}
              {purpose === "showcase" && (
                <p className="fe-community-creator-links">
                  작품을 등록하려면{" "}
                  <Link to="/informate/books/submit">책 등록</Link>
                  <Link to="/informate/vibe-sites/new">웹사이트 등록</Link>
                </p>
              )}
            </section>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-12">
                <label
                  htmlFor="post-title"
                  className="fe-community-field-label"
                >
                  제목
                </label>
                <input
                  id="post-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="fe-community-field"
                  placeholder={
                    communityToolDraft(contextTool?.route || null, purpose)?.placeholder || purposeInfo?.placeholder || "제목을 입력해주세요"
                  }
                />
              </div>

              <details className="lg:col-span-12 fe-community-optional">
                <summary>
                  주제·태그 설정 <span>선택</span>
                </summary>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
                  <div className="lg:col-span-4">
                    <label
                      htmlFor="post-category"
                      className="fe-community-field-label"
                    >
                      관련 주제 (선택)
                    </label>
                    <select
                      id="post-category"
                      value={categoryId}
                      onChange={(e) => {
                        const v = e.target.value ? Number(e.target.value) : "";
                        setCategoryId(v as any);
                        setSubcategoryId("");
                      }}
                      className="fe-community-field"
                    >
                      <option value="">선택 안 함</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="lg:col-span-4">
                    <label
                      htmlFor="post-subcategory"
                      className="fe-community-field-label"
                    >
                      세부 분류
                    </label>
                    <select
                      id="post-subcategory"
                      value={subcategoryId}
                      onChange={(e) =>
                        setSubcategoryId(
                          e.target.value ? Number(e.target.value) : "",
                        )
                      }
                      disabled={!categoryId}
                      className="fe-community-field"
                    >
                      <option value="">선택 안 함</option>
                      {subcategories.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="lg:col-span-4">
                    <label
                      htmlFor="post-tags"
                      className="fe-community-field-label"
                    >
                      태그
                    </label>
                    <input
                      id="post-tags"
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                      className="fe-community-field"
                      placeholder="예: 자료, 평가, 상담"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      콤마(,)로 구분해 입력하세요.
                    </div>
                  </div>
                </div>
              </details>
              {isVibeCodingCategory && (
                <div className="lg:col-span-12">
                  <label
                    htmlFor="post-vibe-link"
                    className="fe-community-field-label"
                  >
                    바이브 코딩 결과 링크
                  </label>
                  <input
                    id="post-vibe-link"
                    type="url"
                    value={vibeCodingLink}
                    onChange={(e) => setVibeCodingLink(e.target.value)}
                    className="fe-community-field"
                    placeholder="https://..."
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    바이브 코딩 결과 페이지 URL을 입력하세요. 게시글 상세에서
                    iframe 미리보기를 시도하고, 제한되면 새 창으로 열 수
                    있습니다.
                  </div>
                </div>
              )}

              <div className="lg:col-span-12">
                <div className="fe-community-editor-toolbar">
                  <span className="fe-community-field-label">내용</span>
                  <button
                    type="button"
                    onClick={() => setPreview((v) => !v)}
                    aria-pressed={preview}
                    className="fe-community-button"
                  >
                    <Eye size={15} />
                    {preview ? "작성으로 돌아가기" : "미리보기"}
                  </button>
                </div>
                {preview ? (
                  <div className="rounded-2xl border border-gray-200 p-4 bg-gray-50 min-h-[360px] text-sm leading-relaxed">
                    <CommunityBody html={sanitizeHtmlForPreview(ensureHtml(contentHtml))} blocks={contentBlocks} preview />
                  </div>
                ) : (
                  <CommunityBodyEditor value={contentHtml} onChange={setContentHtml} blocks={contentBlocks} onBlocksChange={setContentBlocks} initial={!draftPending?initialContent:undefined} onInitialUsed={consumeInitialContent} />
                )}
              </div>

              <div className="lg:col-span-6">
                <label
                  htmlFor="post-image"
                  className="fe-community-field-label"
                >
                  대표 이미지
                </label>
                <input
                  id="post-image"
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const selectionVersion = ++imageSelectionVersion.current;
                    const file = e.target.files?.[0];
                    setHeadImage(null);
                    if (file) {
                      // 이미지 압축 처리
                      setImageCompressing(true);
                      try {
                        const compressed = await compressImage(
                          file,
                          1920,
                          0.85,
                        );
                        if (selectionVersion === imageSelectionVersion.current)
                          setHeadImage(compressed);
                      } finally {
                        if (selectionVersion === imageSelectionVersion.current)
                          setImageCompressing(false);
                      }
                    } else {
                      setImageCompressing(false);
                    }
                  }}
                />
                {imageCompressing && (
                  <p role="status" className="text-xs text-gray-500 mt-2">
                    이미지를 준비하고 있어요…
                  </p>
                )}
                {headImage && (
                  <div className="text-xs text-gray-500 mt-1">
                    선택한 이미지: {headImage.name} ·{" "}
                    {(headImage.size / 1024).toFixed(0)}KB
                  </div>
                )}
                {existing?.head_image_url && (
                  <div className="text-xs text-gray-500 mt-1">
                    기존 이미지가 있으면 새로 선택 시 교체됩니다.
                  </div>
                )}
              </div>

              <div className="lg:col-span-6">
                <label htmlFor="post-file" className="fe-community-field-label">
                  첨부파일
                </label>
                <input
                  id="post-file"
                  type="file"
                  onChange={(e) => setFileUpload(e.target.files?.[0] || null)}
                />
                {existing?.file_upload_name && (
                  <div className="text-xs text-gray-500 mt-1">
                    기존 파일: {existing.file_upload_name}
                  </div>
                )}
              </div>

              <div className="lg:col-span-12">
                <label
                  htmlFor="post-files"
                  className="fe-community-field-label"
                >
                  추가 첨부파일
                </label>
                <input
                  id="post-files"
                  type="file"
                  multiple
                  onChange={(e) =>
                    setAdditionalFiles(
                      e.target.files ? Array.from(e.target.files) : [],
                    )
                  }
                />
                {existing?.additional_files?.length ? (
                  <div className="mt-3 border border-gray-200 rounded-2xl p-4">
                    <div className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Paperclip className="w-4 h-4" />
                      기존 추가 파일
                    </div>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {existing.additional_files.map((f) => (
                        <li key={f.id} className="truncate">
                          {f.url ? (
                            <a
                              className="text-[#526a2e] hover:underline"
                              href={f.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {f.filename}
                            </a>
                          ) : (
                            f.filename
                          )}
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs text-gray-500 mt-2">
                      삭제는 상세 화면에서 개별 삭제 가능합니다.
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="fe-community-editor-footer">
              <p role="status" className="fe-community-save-status">
                <Check size={14} />
                {draftStatus ||
                  (draftPending
                    ? "임시저장 복구 여부를 먼저 선택해 주세요."
                    : "글은 자동으로 임시저장돼요.")}
                <span>첨부파일은 게시할 때 저장돼요.</span>
              </p>
              <div className="flex gap-2">
                <Link
                  to={
                    editing && id
                      ? `/community/board/${id}`
                      : "/community/board"
                  }
                  className="px-4 py-2.5 rounded-xl font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                >
                  취소
                </Link>
                <button
                  onClick={submit}
                  disabled={saving || imageCompressing || loading}
                  className="fe-primary-action inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold bg-gradient-to-r from-indigo-600 to-blue-600 text-white hover:shadow-lg transition disabled:opacity-60"
                >
                  <Save className="w-4 h-4" />
                  {saving ? "저장 중…" : editing ? "수정 완료" : "이야기 게시"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
